import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

import { CatalogPlan, CatalogVocabulary } from '../models/catalog.plan.model';
import { CatalogAddOn } from '../models/catalog.addon.model';
import { ApiDigitalAuthCatalog } from '../services/auth/catalog.service';
import { authApiKey } from '../scripts/authentication.script';
import { guardCatalogResponse } from '../scripts/response.shape.guard';

const VOCAB_STORAGE_KEY = 'da_catalog_vocabulary';

function loadVocabFromSession(): CatalogVocabulary | null {
  try {
    const raw = sessionStorage.getItem(VOCAB_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveVocabToSession(v: CatalogVocabulary | null): void {
  try {
    if (v) {
      sessionStorage.setItem(VOCAB_STORAGE_KEY, JSON.stringify(v));
    } else {
      sessionStorage.removeItem(VOCAB_STORAGE_KEY);
    }
  } catch {
    // sessionStorage full or unavailable
  }
}

interface CatalogContextState {
  plans: CatalogPlan[];
  addons: CatalogAddOn[];
  vocabulary: CatalogVocabulary | null;
  loading: boolean;
  currentPlanId: string | null;
  currentBillingCycle: string | null;
  currentPeriodEnd: string | null;
  cheapestPlanForFeature: (featureKey: string) => CatalogPlan | null;
}

const CatalogContext = createContext<CatalogContextState>({
  plans: [],
  addons: [],
  vocabulary: null,
  loading: false,
  currentPlanId: null,
  currentBillingCycle: null,
  currentPeriodEnd: null,
  cheapestPlanForFeature: () => null,
});

export const CatalogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [plans, setPlans] = useState<CatalogPlan[]>([]);
  const [addons, setAddons] = useState<CatalogAddOn[]>([]);
  const [vocabulary, setVocabulary] = useState<CatalogVocabulary | null>(loadVocabFromSession);
  const [loading, setLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [currentBillingCycle, setCurrentBillingCycle] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const didFetch = useRef(false);

  const fetchPlans = useCallback(async () => {
    const apiKey = authApiKey();
    if (!apiKey) return;

    setLoading(true);
    try {
      const [plansResponse, addonsResponse] = await Promise.all([
        ApiDigitalAuthCatalog.getPlans(apiKey),
        ApiDigitalAuthCatalog.getAddOns(apiKey),
      ]);

      if (plansResponse.status === 200 && plansResponse.data) {
        const data = plansResponse.data;
        guardCatalogResponse(data);
        const planList = Array.isArray(data) ? data : (data?.plans ?? data?.items ?? []);
        setPlans(planList);
        if (data?.vocabulary) {
          setVocabulary(data.vocabulary);
          saveVocabToSession(data.vocabulary);
        }
        if (data?.current_plan_id) setCurrentPlanId(data.current_plan_id);
        if (data?.current_billing_cycle) setCurrentBillingCycle(data.current_billing_cycle);
        if (data?.current_period_end) setCurrentPeriodEnd(data.current_period_end);
      } else {
        setPlans([]);
      }

      if (addonsResponse.status === 200 && addonsResponse.data) {
        const addonData = addonsResponse.data;
        const addonList: CatalogAddOn[] = Array.isArray(addonData) ? addonData : (addonData?.addons ?? []);
        // Normalize _id → id
        for (const a of addonList) {
          if (!a.id && a._id) a.id = a._id;
        }
        setAddons(addonList);
      } else {
        setAddons([]);
      }
    } catch {
      setPlans([]);
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const cheapestPlanForFeature = useCallback((featureKey: string): CatalogPlan | null => {
    if (!plans.length) return null;

    const matching = plans.filter(p =>
      p.features && typeof p.features === 'object' && p.features[featureKey] === true
    );

    if (!matching.length) return null;

    matching.sort((a, b) => {
      if (a.is_free && !b.is_free) return -1;
      if (!a.is_free && b.is_free) return 1;
      const orderA = a.sort_order ?? 999;
      const orderB = b.sort_order ?? 999;
      return orderA - orderB;
    });

    return matching[0];
  }, [plans]);

  useEffect(() => {
    if (!didFetch.current) {
      didFetch.current = true;
      fetchPlans();
    }
  }, [fetchPlans]);

  return (
    <CatalogContext.Provider value={{
      plans, addons, vocabulary, loading,
      currentPlanId, currentBillingCycle, currentPeriodEnd,
      cheapestPlanForFeature,
    }}>
      {children}
    </CatalogContext.Provider>
  );
};

export function useCatalog(): CatalogContextState {
  return useContext(CatalogContext);
}
